import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import tokenExpirationHandler from '../utils/tokenExpirationHandler';
import setupAxiosInterceptor from '../utils/axiosInterceptor';
import TokenExpirationModal from '../components/TokenExpirationModal';

const TokenExpirationContext = createContext();

export const useTokenExpiration = () => {
  const context = useContext(TokenExpirationContext);
  if (!context) {
    throw new Error('useTokenExpiration must be used within a TokenExpirationProvider');
  }
  return context;
};

export const TokenExpirationProvider = ({ children }) => {
  const [showExpirationModal, setShowExpirationModal] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isExpired, setIsExpired] = useState(false);
  const navigate = useNavigate();

  // Callback cuando el token está próximo a expirar
  const handleTokenExpiringSoon = (minutesLeft) => {
    setTimeRemaining(minutesLeft);
    setShowExpirationModal(true);
    setIsExpired(false);
  };

  // Callback cuando el token ha expirado
  const handleTokenExpired = () => {
    setIsExpired(true);
    setShowExpirationModal(true);
  };

  // Extender sesión
  const extendSession = async () => {
    try {
      setShowExpirationModal(false);
      setTimeRemaining(0);
      setIsExpired(false);
      
      // Reiniciar el monitoreo
      tokenExpirationHandler.startMonitoring();
    } catch (error) {
      console.error('Error extendiendo sesión:', error);
      handleTokenExpired();
    }
  };

  // Cerrar modal y redirigir al login
  const handleCloseModal = () => {
    setShowExpirationModal(false);
    setIsExpired(false);
    setTimeRemaining(0);
    
    // Limpiar token y redirigir
    localStorage.removeItem('token');
    tokenExpirationHandler.stopMonitoring();
    navigate('/login');
  };

  // Configurar interceptors y handlers
  useEffect(() => {
    // Configurar callbacks del token handler
    tokenExpirationHandler.setOnWarningCallback(handleTokenExpiringSoon);
    tokenExpirationHandler.setOnExpirationCallback(handleTokenExpired);

    // Configurar interceptor de axios
    const cleanupAxios = setupAxiosInterceptor(handleTokenExpired);

    // Iniciar monitoreo si hay token
    if (localStorage.getItem('token')) {
      tokenExpirationHandler.startMonitoring();
    }

    // Cleanup al desmontar
    return () => {
      cleanupAxios();
      tokenExpirationHandler.stopMonitoring();
    };
  }, []);

  const value = {
    showExpirationModal,
    timeRemaining,
    isExpired,
    extendSession,
    handleCloseModal,
    isTokenValid: tokenExpirationHandler.isTokenValid(),
    getTimeRemaining: tokenExpirationHandler.getTimeRemaining()
  };

  return (
    <TokenExpirationContext.Provider value={value}>
      {children}
      <TokenExpirationModal
        isOpen={showExpirationModal}
        onClose={handleCloseModal}
        onExtendSession={extendSession}
        timeRemaining={timeRemaining}
        isExpired={isExpired}
      />
    </TokenExpirationContext.Provider>
  );
};
